import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { invoke } from "./tauri-web";
import { listen } from "./tauri-web";
import { platform } from "./tauri-web";
import { open as openFolderDialog } from "@tauri-apps/plugin-dialog";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac, openAccountDelete } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { AddFundsMenu } from "./AddFundsMenu";
import {
  store, langLabel, fmtBytes, fmtCount, snippetCite, relativeTime, parseCitations,
  SYMBOL_KIND_LABELS, type Repo, type FileRow, type Snippet, type Symbol, type HistoryEntry, type IndexSummary,
  type Freshness,
} from "./store";
import { askCodebase, extractCodeFromPhoto, openWebLogin, BrainError, type BrainErrorCode, type AskCitation } from "./brain";
import { SnapCodeButton } from "./chat/photo";
import { OrioraBadge } from "./OrioraBadge";
import { BackupMenu } from "./BackupMenu";
import Chat from "./chat/Chat";
import {
  Sparkles, Code2, FolderSearch, Trash2, FileCode, WifiOff, RefreshCw, FolderPlus, Search,
  ShieldCheck, Copy, Check, AlertTriangle, Wallet, KeyRound, Braces, History, ArrowRight, X, Camera,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Oriora KnowCode — ASK YOUR CODEBASE.
//
// Point the app at a local folder (a git repo); it indexes it ON-DEVICE (walk + chunk + symbol-parse
// in Rust → local SQLite). Ask plain-English questions ("where is auth handled?", "explain this
// module"); the app retrieves the most relevant code snippets LOCALLY and gets a grounded answer with
// file:line citations. PRIVACY is the selling point: only the few retrieved snippets cross to the LLM,
// never the whole repo. BYOK-only — the customer connects their own vendor key in Oriora Settings.
// ─────────────────────────────────────────────────────────────────────────────

const APP_SCHEME = "oriora-knowcode";
const APP_NAME = "Oriora KnowCode";

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD §2/§3, Apple Guideline 5.1.1(i)). Shown ONCE on first launch.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still returns it.
  // getUser() asks the SERVER — on a real auth rejection (401/403) we purge the dead session.
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground (catches "deleted my account, came back").
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns oriora-knowcode://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window navigated to oriora-knowcode://deleted.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // First-launch AI-consent gate — before login + before any AI feature is reachable.
  if (!consented) {
    return <AiConsent brand={APP_NAME}
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted) return <BootScreen />;
  if (!authed) return <Login />;
  return <Home email={email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

// ── Boot / splash (replaces a bare "Loading…") ──────────────────────────────────
function BootScreen() {
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "1.5rem" }}>
      <div style={{ display: "grid", placeItems: "center", gap: "0.75rem" }}>
        <span style={{ fontSize: "2rem" }}><Code2 size={32} color="var(--accent)" /></span>
        <div className="dim" style={{ fontSize: "var(--text-sm)" }}>Opening {APP_NAME}…</div>
      </div>
    </main>
  );
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="hero" style={{ width: "100%", maxWidth: 400, textAlign: "center", padding: "2.25rem 2rem" }}>
        <div style={{ display: "inline-flex", padding: "0.85rem", borderRadius: "var(--radius-lg)", background: "var(--accent-soft)", marginBottom: "1rem" }}>
          <Code2 size={28} color="var(--accent)" />
        </div>
        <div className="label" style={{ color: "var(--accent)", justifyContent: "center" }}>{APP_NAME}</div>
        <h1 className="h1" style={{ margin: "0.5rem 0 0.6rem" }}>Ask your codebase</h1>
        <p className="muted" style={{ fontSize: "var(--text-sm)", lineHeight: 1.6, marginBottom: "1.5rem" }}>
          Sign in with your Oriora account. Your code is indexed on this device — only the few snippets
          that match a question ever leave it.
        </p>
        <button className="btn btn-primary" style={{ width: "100%" }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            // iOS → in-app ASWebAuthenticationSession (Apple Guideline 4, #61); desktop → in-app login window.
            try { const p = platform(); if (p === "ios") await signInIos(APP_SCHEME); else if (p === "macos") await signInMac(APP_SCHEME); else if (p === "web") window.location.href = "/login?next=/oriora-knowcode/"; else await openWebLogin(); }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? <><Spinner /> Signing in…</> : "Sign in with Oriora"}
        </button>
        <p className="caption" style={{ marginTop: "1rem", display: "inline-flex", gap: "0.35rem", alignItems: "center", justifyContent: "center" }}>
          <ShieldCheck size={12} color="var(--success)" /> Your code lives on this device — you choose what's sent or backed up
        </p>
      </div>
    </main>
  );
}

function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  const [repos, setRepos] = useState<Repo[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(false);
  const [indexing, setIndexing] = useState(false);
  const [indexErr, setIndexErr] = useState<string | null>(null);
  // What the last index pass left OUT. `IndexSummary.skipped` has always been returned by both
  // surfaces and was never rendered — so the strictest thing the app does was invisible.
  const [lastSkipped, setLastSkipped] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Repo | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  const [chatOpen, setChatOpen] = useState(false);

  const askRef = useRef<HTMLDivElement>(null);
  const symbolsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const reload = useCallback(async () => {
    setLoadErr(false);
    try {
      const r = await store.listRepos();
      setRepos(r);
      setActiveId((prev) => (prev && r.some((x) => x.id === prev) ? prev : r[0]?.id ?? null));
    } catch {
      setLoadErr(true);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  // The ON-DEVICE index pass: choose a folder, walk + chunk + symbol-parse in Rust, store in SQLite.
  const indexFolder = useCallback(async () => {
    setIndexErr(null);
    setLastSkipped(null);
    // Web: no Tauri dialog bridge — use the browser's own folder picker
    // (<input webkitdirectory>); the picked files are read here, on-device,
    // and handed to the same indexer (knowcode-web.ts port of walker.rs).
    let onWeb = false;
    try { onWeb = platform() === "web"; } catch { onWeb = false; }
    if (onWeb) {
      const input = document.createElement("input");
      input.type = "file";
      (input as HTMLInputElement & { webkitdirectory: boolean }).webkitdirectory = true;
      input.style.display = "none";
      document.body.appendChild(input);
      const filesPicked = await new Promise<File[]>((resolve) => {
        input.onchange = () => resolve([...(input.files ?? [])]);
        // A cancelled picker fires no event on some browsers; a focus fallback resolves empty.
        window.addEventListener("focus", () => setTimeout(() => resolve([...(input.files ?? [])]), 400), { once: true });
        input.click();
      });
      input.remove();
      if (filesPicked.length === 0) return;
      setIndexing(true);
      try {
        const SKIP = new Set([".git", ".hg", ".svn", "node_modules", "target", "dist", "build", "out", ".next", ".nuxt", ".venv", "venv", "__pycache__", ".mypy_cache", ".pytest_cache", ".cargo", "vendor", "Pods", ".gradle", ".idea", ".vscode", "coverage", ".cache", ".turbo", "bin", "obj", ".terraform", "DerivedData", ".dart_tool", "Carthage", "bower_components", ".expo", ".parcel-cache"]);
        const relOf = (f: File) => ((f as File & { webkitRelativePath?: string }).webkitRelativePath ?? f.name);
        const rootName = (relOf(filesPicked[0]).split("/")[0] ?? "repository") || "repository";
        const files: { path: string; text: string }[] = [];
        for (const f of filesPicked) {
          const segments = relOf(f).split("/").filter(Boolean);
          const rel = segments.slice(1).join("/"); // strip the picked folder's own name
          if (rel === "") continue;
          if (segments.slice(1, -1).some((d) => SKIP.has(d))) continue; // don't even read vendored trees
          if (f.size > 600 * 1024) continue; // walker's per-file ceiling; skipped count handled by indexer for the rest
          files.push({ path: rel, text: await f.text() });
        }
        const summary: IndexSummary = await store.indexFolderWeb(rootName, files);
        setLastSkipped(summary.skipped);
        setActiveId(summary.repo.id);
        await reload();
      } catch (e) {
        setIndexErr(`Couldn't index that folder — try another. (${e instanceof Error ? e.message : String(e)})`);
      } finally {
        setIndexing(false);
      }
      return;
    }
    let picked: string | null = null;
    try {
      const sel = await openFolderDialog({ directory: true, multiple: false, title: "Choose a project folder to index" });
      picked = Array.isArray(sel) ? sel[0] ?? null : sel;
    } catch (e) {
      setIndexErr(`Couldn't open the folder picker. (${e instanceof Error ? e.message : String(e)})`);
      return;
    }
    if (!picked) return;
    setIndexing(true);
    try {
      const summary: IndexSummary = await store.indexFolder(picked);
      setLastSkipped(summary.skipped);
      setActiveId(summary.repo.id);
      await reload();
    } catch (e) {
      setIndexErr(`Couldn't index that folder — try another. (${e instanceof Error ? e.message : String(e)})`);
    } finally {
      setIndexing(false);
    }
  }, [reload]);

  // Native-menu actions (Actions menu in the macOS menu bar): Index / Ask / Symbols.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      switch (e.payload) {
        case "index": void indexFolder(); break;
        case "ask":
          askRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          askRef.current?.querySelector<HTMLInputElement>("input")?.focus();
          break;
        case "symbols": symbolsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); break;
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, [indexFolder]);

  const active = useMemo(() => repos.find((r) => r.id === activeId) ?? null, [repos, activeId]);

  async function doDelete(id: string) {
    setConfirmDelete(null);
    await store.deleteRepo(id);
    await reload();
  }

  return (
    <main style={{
      maxWidth: 760, margin: "0 auto",
      padding: "max(2rem, env(safe-area-inset-top)) max(1.5rem, env(safe-area-inset-right)) calc(2rem + env(safe-area-inset-bottom)) max(1.5rem, env(safe-area-inset-left))",
    }}>
      {offline && (
        <div role="status" className="banner banner-warning" style={{ marginBottom: "1rem" }}>
          <WifiOff size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>Answering paused — your indexed code and symbol map still work offline.</span>
        </div>
      )}

      <header className="row" style={{ justifyContent: "space-between", marginBottom: "1.5rem", alignItems: "flex-start" }}>
        <div className="row" style={{ gap: "0.6rem" }}>
          {/* Design port 2026-08-26: logo tile + mono wordmark (deliverable screens 02–05). The
              PROMISE verb now leads inside the input card's placeholder ("Ask about this codebase…"). */}
          <span className="kc-tile" aria-hidden>&gt;_</span>
          <div>
            <h1 className="kc-wordmark">knowcode</h1>
            <div className="caption">{repos.length === 0 ? "on this device" : `${repos.length} ${repos.length === 1 ? "repo" : "repos"} · on this device`}</div>
          </div>
        </div>
        <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
          {repos.length > 0 && (
            <button className="btn-ghost" style={{ fontSize: "var(--text-xs)", padding: "0.25rem 0.5rem" }} onClick={() => void indexFolder()} disabled={indexing} title="Index another folder">
              <FolderPlus size={13} /> Index
            </button>
          )}
          <button className="btn-ghost" style={{ fontSize: "var(--text-xs)", padding: "0.25rem 0.5rem" }} onClick={() => setShowHistory(true)}>
            <History size={13} /> History
          </button>
          <BackupMenu
            appName="Knowcode" fileSlug="knowcode" appScheme="oriora-knowcode"
            hasData={repos.length > 0} onRestored={reload}
          />
          <AccountMenu email={email} appSlug="oriora-knowcode" appName="Oriora KnowCode" onSignedOut={onSignedOut} />
          <AddFundsMenu />
        </div>
      </header>

      {/* BYOK banner — this is a BYOK-only app; the customer connects their own vendor key. */}
      <div className="banner banner-info" style={{ marginBottom: "1rem" }}>
        <KeyRound size={15} style={{ color: "var(--info)", flexShrink: 0, marginTop: 1 }} />
        <span style={{ fontSize: "var(--text-xs)", lineHeight: 1.5 }}>
          Bring your own key. Answers run on your own AI vendor key — connect it once in your Oriora
          Settings, then ask away. Indexing and search are fully on-device.
        </span>
      </div>

      {indexErr && (
        <div className="banner banner-danger" style={{ marginTop: "0.75rem" }}>
          <AlertTriangle size={15} style={{ color: "var(--danger)", flexShrink: 0, marginTop: 1 }} />
          <span style={{ fontSize: "var(--text-xs)", lineHeight: 1.5 }}>{indexErr}</span>
        </div>
      )}

      {/* What the pass left out. Deliberately does NOT itemise which files — the indexer returns a
          count, not a list, and naming a skipped path is not something this number can support. */}
      {lastSkipped !== null && lastSkipped > 0 && !indexing && (
        <div role="note" style={{
          marginTop: "0.75rem", padding: "0.6rem 0.8rem", borderRadius: "var(--radius-md, 10px)",
          border: "1px solid var(--border-hairline)", background: "var(--bg-surface)",
          fontSize: "var(--text-xs)", lineHeight: 1.55, color: "var(--text-secondary)",
        }}>
          <span className="tnum" style={{ color: "var(--text-primary)", fontWeight: 600 }}>{lastSkipped}</span>
          {" "}file{lastSkipped === 1 ? "" : "s"} left out of the index — anything that looks like a secret
          (<code>.env</code>, private keys, credential stores), plus binaries, oversized files, and file
          types with no source in them.
        </div>
      )}

      {loadErr ? (
        <div className="card-raised" style={{ padding: "1.75rem 1.5rem", textAlign: "center", marginTop: "1.5rem" }}>
          <AlertTriangle size={26} color="var(--danger)" />
          <div className="h3" style={{ margin: "0.5rem 0 0.25rem" }}>Couldn't load your index</div>
          <div className="muted" style={{ fontSize: "var(--text-sm)", marginBottom: "1rem" }}>Something went wrong reading your indexed repos on this device.</div>
          <button className="btn" onClick={() => { setLoading(true); void reload(); }}><RefreshCw size={14} /> Retry</button>
        </div>
      ) : loading ? (
        <div style={{ marginTop: "1.5rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
          {[0, 1].map((i) => (
            <div key={i} className="card" style={{ padding: "1rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
              <div className="skeleton-line" style={{ width: "40%", height: 16 }} />
              <div className="skeleton-line" style={{ width: "70%" }} />
            </div>
          ))}
        </div>
      ) : !active ? (
        <div className="card-raised" style={{ padding: "2.25rem 1.5rem", textAlign: "center", marginTop: "1.5rem" }}>
          {/* Design port 2026-08-26 (deliverable screen 02). Body copy kept from the approved §7b
              retention framing — the deliverable's "Nothing leaves this device" absolute and its
              "keeps it updated as files change" claim are both things the app doesn't promise. */}
          <span className="kc-tile lg" aria-hidden style={{ margin: "0 auto" }}>&gt;_</span>
          <div className="h3" style={{ margin: "0.85rem 0 0.4rem" }}>Index a folder to get started</div>
          <div className="muted" style={{ fontSize: "var(--text-sm)", maxWidth: 440, margin: "0 auto 1.25rem", lineHeight: 1.6 }}>
            Point KnowCode at a local folder — a git repo, a service, anything with source files. It
            walks + indexes it on your device, then lets you ask plain-English questions and get answers
            with exact file&nbsp;:&nbsp;line citations.
          </div>
          <button className="btn btn-primary" onClick={() => void indexFolder()} disabled={indexing}>
            {indexing ? <><Spinner /> Indexing…</> : <><FolderSearch size={16} /> Index a folder</>}
          </button>
          <WhatIndexingGivesYou />
        </div>
      ) : (
        <>
          {/* THE ASK — position 1, the promise verb, focused on load. */}
          <div ref={askRef} style={{ marginTop: "1.25rem" }}>
            <AskBox repo={active} offline={offline} repos={repos} activeId={activeId} onSwitchRepo={setActiveId} />
          </div>

          <div ref={symbolsRef} style={{ marginTop: "1.5rem" }}>
            <SymbolMap repo={active} />
          </div>

          <FileList repo={active} />

          {/* Index status — demoted to setup context beneath the ask. The freshness trust signal +
              the files·symbols·bytes tally live here now, not in a hero at the top of the screen. */}
          <div className="row" style={{ justifyContent: "space-between", margin: "1.75rem 0 0.75rem", alignItems: "center" }}>
            <h2 className="h3" style={{ margin: 0 }}>Index</h2>
            <div className="row" style={{ gap: "0.4rem" }}>
              <button className="btn-ghost" title="Index another folder" style={{ fontSize: "var(--text-xs)", padding: "0.3rem 0.55rem" }} onClick={() => void indexFolder()} disabled={indexing}>
                <FolderPlus size={13} /> Index another
              </button>
              <button className="btn-ghost" title="Remove this index" aria-label="Remove this index"
                style={{ fontSize: "var(--text-xs)", padding: "0.3rem 0.55rem", color: "var(--text-tertiary)" }}
                onClick={() => setConfirmDelete(active)}>
                <Trash2 size={13} /> Remove
              </button>
            </div>
          </div>
          <RepoHero repo={active} repoCount={repos.length} indexing={indexing} loading={loading} />
        </>
      )}

      {confirmDelete && (
        <ModalShell title="Remove this index?" onCancel={() => setConfirmDelete(null)}>
          <p className="muted" style={{ fontSize: "var(--text-sm)", margin: "0 0 1.25rem", lineHeight: 1.6 }}>
            Remove the index of “{confirmDelete.name}”? Your source files are untouched on disk — only
            the on-device index (chunks + symbol map) is cleared. You can re-index any time.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
            <button className="btn" onClick={() => setConfirmDelete(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={() => doDelete(confirmDelete.id)}><Trash2 size={14} /> Remove index</button>
          </div>
        </ModalShell>
      )}

      {showHistory && <HistoryPanel onClose={() => setShowHistory(false)} />}

      {/* Chat panel — a natural-language way to use the app by typing. Purely additive: the launcher
          + slide-over live on top; no existing screen is altered. A chat-driven change reloads the repo list. */}
      {!chatOpen && (
        <button className="kdc-launch" onClick={() => setChatOpen(true)} aria-label="Open chat assistant" title="Chat">
          &gt;_
        </button>
      )}
      <aside className={`kdc-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="kdc-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Assistant — type to index, ask, or manage repos</span>
          <button className="link" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={reload} activeRepoId={activeId} />
      </aside>
    </main>
  );
}

/**
 * What indexing buys, before a folder is picked — the two free modes and the two guarantees,
 * each bound to the code that makes it true:
 *   · Search + "where is X defined?" run off the local FTS5 index and the symbol map
 *     (knowcode-web.ts / db.rs), with NO call to brain.ts — so they cost nothing;
 *   · `askCodebase` (brain.ts) posts exactly `{ question, snippets:[{path,lang,start_line,
 *     end_line,code}], now_ts }` — no repo name, no file tree, no whole file — which is why the
 *     sentence names the question and the matched snippets and stops there;
 *   · `looksSecret` (knowcode-web.ts, ported from walker.rs) drops env files, private keys and
 *     credential stores BEFORE chunking, so they cannot be retrieved into those snippets;
 *   · §7b: Back up relays the store to the user's own cloud, so the retention sentence carries
 *     the approved qualifier on its own line rather than an absolute.
 */
function WhatIndexingGivesYou() {
  const row = {
    display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 2,
    padding: "0.7rem 0", borderTop: "1px solid var(--border-hairline)", textAlign: "left" as const,
  };
  const body = { margin: 0, fontSize: "var(--text-sm)", lineHeight: 1.55, color: "var(--text-secondary)" };
  return (
    <div style={{ maxWidth: 440, margin: "1.5rem auto 0" }}>
      <div style={row}>
        <span className="label">Free, on this device</span>
        <p style={body}>
          Search the code, and jump to <b style={{ color: "var(--text-primary)" }}>where a symbol is
          defined</b> — file and line, straight from the index. Neither asks the model anything, so
          neither costs a thing.
        </p>
      </div>
      <div style={row}>
        <span className="label">When you do ask</span>
        <p style={body}>
          Only your question and the snippets that matched it are sent, each with its path and line
          range. Not the folder, not the file list, not a whole file.
        </p>
      </div>
      <div style={row}>
        <span className="label">Kept out</span>
        <p style={body}>
          Files that look like secrets — <code>.env</code>, private keys, credential stores — are
          skipped before anything is chunked, so they can never end up in a snippet.
        </p>
        {/* The sentence above is a GUARANTEE, and it was false for one real customer: the skip
            landed on web in `0d18681` (2026-09-19) and fixes NEW indexes only, so a repo indexed
            before that keeps its `.env` rows until it is re-indexed (`knowcode-web.ts:49-50`).
            Qualified here rather than softened — the guarantee is true, it just has a start date.
            Its own <p> so the per-line copy gate still matches a whole sentence per line. */}
        <p style={body}>
          Indexes built before this was added keep their old rows until you re-index.
        </p>
      </div>
      <p style={{ ...body, fontSize: "var(--text-xs)", color: "var(--text-tertiary)", borderTop: "1px solid var(--border-hairline)", paddingTop: "0.7rem", textAlign: "left" }}>
        The index stays on this device unless you back it up, and questions are charged per ask on your own key — no subscription.
      </p>
    </div>
  );
}

function RepoHero({ repo, repoCount, indexing, loading }: { repo: Repo | null; repoCount: number; indexing: boolean; loading: boolean }) {
  const [langs, setLangs] = useState<[string, number][]>([]);
  // Row 2b — the DECISION, not the tally. `files · symbols · bytes` says what exists; what the user
  // needs to know before trusting an answer is whether the index still matches the folder on disk.
  const [fresh, setFresh] = useState<Freshness | null>(null);
  useEffect(() => {
    let alive = true;
    if (repo) {
      store.languageBreakdown(repo.id).then((l) => { if (alive) setLangs(l); }).catch(() => setLangs([]));
      // Web has no local folder to scan, so this legitimately fails there — leave it null and show
      // nothing rather than claiming "fresh" on a surface that cannot know.
      store.repoFreshness(repo.id).then((f) => { if (alive) setFresh(f); }).catch(() => setFresh(null));
    } else { setLangs([]); setFresh(null); }
    return () => { alive = false; };
  }, [repo]);

  if (indexing) {
    return (
      <div className="hero row" style={{ gap: "1rem", alignItems: "center" }}>
        <Spinner />
        <div style={{ flex: 1, display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
          <div style={{ fontWeight: 600, fontSize: "var(--text-sm)" }}>Indexing on your device…</div>
          {/* §7b — "your code never leaves this machine" was FALSE on two counts: the matched
              snippets ARE sent per question (brain.ts::askCodebase), and the Back-up panel (<BackupMenu>)
              relays the whole store to the user's own cloud on Back up. Indexing itself is genuinely
              local, which is what this line is about — so keep that, drop the absolute. Approved
              Lockbox form: the promise is RETENTION, never visibility. */}
          <div className="muted" style={{ fontSize: "var(--text-xs)" }}>Walking files, chunking and parsing symbols — all on this machine. Your code stays here unless you back it up; only the snippets that match a question pass through Oriora, which doesn&apos;t retain them.</div>
        </div>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="hero row" style={{ gap: "1rem" }}>
        <Braces size={36} color="var(--accent)" style={{ opacity: 0.5 }} />
        <div style={{ flex: 1, display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
          <div className="skeleton-line" style={{ width: "60%", height: 18 }} />
          <div className="skeleton-line" style={{ width: "40%" }} />
        </div>
      </div>
    );
  }
  return (
    <div className="hero row" style={{ gap: "1rem", alignItems: "center" }}>
      <span style={{ display: "inline-flex", flexShrink: 0 }}><Braces size={36} color="var(--accent)" /></span>
      <div style={{ minWidth: 0, flex: 1 }}>
        {repo ? (
          <>
            <div className="label" style={{ color: "var(--accent)" }}>Indexed on this device</div>
            <div className="h2" style={{ margin: "0.15rem 0 0.3rem", lineHeight: 1.15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{repo.name}</div>
            {/* THE DECISION FIRST. A stale index cites code that has since changed — confidently
                wrong answers are the worst failure this app can have, so it leads. `unreadable` is a
                THIRD state (folder moved/deleted) and must never render as "fresh". */}
            {fresh && (
              <div
                className="row"
                style={{
                  gap: "0.4rem", marginBottom: "0.35rem", fontSize: "var(--text-sm)", fontWeight: 600,
                  color: fresh.unreadable ? "var(--danger, #c0544a)"
                       : fresh.changed > 0 ? "var(--warn, #c49a3c)"
                       : "var(--ok, #6b9e72)",
                }}
              >
                {fresh.unreadable
                  ? "Folder not found — re-index to point it somewhere that exists"
                  : fresh.changed > 0
                    ? `${fmtCount(fresh.changed)} of ${fmtCount(fresh.scanned)} files changed since indexing — answers may cite old code`
                    : "Index matches the folder on disk"}
              </div>
            )}
            {fresh && fresh.changed > 0 && fresh.examples.length > 0 && (
              <div className="muted" style={{ fontSize: "var(--text-xs)", marginBottom: "0.3rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                e.g. {fresh.examples.join(", ")}
              </div>
            )}
            <div className="muted tnum" style={{ fontSize: "var(--text-sm)" }}>
              {fmtCount(repo.file_count)} files · {fmtCount(repo.symbol_count)} symbols · {fmtBytes(repo.byte_count)}
            </div>
            {langs.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginTop: "0.55rem" }}>
                {langs.slice(0, 6).map(([l, n]) => (
                  <span key={l} className="cite"><span className="tnum">{n}</span> {langLabel(l)}</span>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="h3" style={{ margin: 0 }}>Ask your codebase</div>
            <div className="muted" style={{ fontSize: "var(--text-sm)", marginTop: 4 }}>
              {repoCount === 0 ? "Index a folder to start asking questions about your code." : "Pick a repo to ask about."}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── The ONE AI feature: ask your codebase (cited, grounded) ──
type AskErr = { code: BrainErrorCode | "empty"; message: string };

function AskBox({ repo, offline, repos, activeId, onSwitchRepo }: { repo: Repo; offline: boolean; repos: Repo[]; activeId: string | null; onSwitchRepo: (id: string) => void }) {
  const [mode, setMode] = useState<"ask" | "search">("ask");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState<"idle" | "retrieving" | "asking">("idle");
  const [answer, setAnswer] = useState<string | null>(null);
  const [citations, setCitations] = useState<AskCitation[]>([]);
  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const [confidence, setConfidence] = useState(0);
  const [err, setErr] = useState<AskErr | null>(null);
  const [copied, setCopied] = useState(false);
  // Free, on-device, UNBILLED code search — the FTS5 retrieval that used to run only inside a billed
  // ask, surfaced as its own co-equal mode (Sourcebot's Search/Ask pair). No peer offers this on a
  // phone with the index never leaving the device.
  const [searchResults, setSearchResults] = useState<Snippet[] | null>(null);
  const [searching, setSearching] = useState(false);
  // Design port 2026-08-26 — presentational only: which evidence-rail snippet is expanded
  // (first open by default, per deliverable screen 04), and the term to mark in search hits.
  const [openEv, setOpenEv] = useState(0);
  const [searchedFor, setSearchedFor] = useState("");
  // "Snap code" — photograph a snippet/error/diagram; the brain reads the text out of it (one billed
  // image call), then we run the ordinary on-device search with that text. `snapping` is its own busy
  // flag; `snapErr` is the photo-specific "couldn't read it" line (brain-level errors reuse setErr).
  const [snapping, setSnapping] = useState(false);
  const [snapErr, setSnapErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // A citation's snippet in the evidence rail: same file, overlapping line range.
  const evidenceIndexFor = (c: AskCitation) =>
    snippets.findIndex((s) => s.path === c.path && c.start_line <= s.end_line && c.end_line >= s.start_line);

  // Reset when the active repo changes (an answer/search about repo A is wrong for repo B).
  useEffect(() => {
    setAnswer(null); setCitations([]); setSnippets([]); setConfidence(0); setErr(null); setQ(""); setSearchResults(null); setOpenEv(0); setSnapErr(null);
  }, [repo.id]);
  // Lead with the ask — focus the question box when a repo is ready.
  useEffect(() => { inputRef.current?.focus(); }, [repo.id]);

  async function runSearch(query = q) {
    const trimmed = query.trim();
    if (!trimmed || searching) return;
    setSearching(true); setSearchResults(null); setSearchedFor(trimmed); setSnapErr(null);
    try {
      setSearchResults(await store.searchCode(repo.id, trimmed, 20));
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }

  // Snap code: photo → brain reads the text out of it → run the on-device search with that text.
  async function onSnap(dataUrl: string) {
    if (snapping) return;
    setSnapping(true); setSnapErr(null); setErr(null);
    try {
      const extracted = await extractCodeFromPhoto(dataUrl);
      // FTS tokenises anyway, so whitespace/newlines don't matter — collapse for a clean query box,
      // and cap the length so an over-eager transcription can't build a pathological MATCH expression.
      const queryText = extracted.replace(/\s+/g, " ").trim().slice(0, 400);
      if (!queryText) {
        setSnapErr("Couldn't read any code or text in that photo. Try a closer, sharper shot.");
        return;
      }
      setMode("search");
      setQ(queryText);
      await runSearch(queryText);
    } catch (e) {
      // Brain-level failures (no key / no funds / offline) reuse the rich AskError surface.
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setSnapErr("Couldn't read that photo — please try again.");
    } finally {
      setSnapping(false);
    }
  }

  const EXAMPLES = [
    "Where is authentication handled?",
    "Explain how the database layer works.",
    "What does the main entry point do?",
  ];

  async function run(question = q) {
    const trimmed = question.trim();
    if (!trimmed || busy) return;
    setBusy(true); setErr(null); setAnswer(null); setCitations([]); setSnippets([]); setConfidence(0); setCopied(false); setOpenEv(0); setSnapErr(null);
    try {
      // 1) Local FTS5 retrieval (on-device) — the top matching code snippets for THIS repo.
      setStage("retrieving");
      const hits: Snippet[] = await store.searchCode(repo.id, trimmed, 10);
      setSnippets(hits);
      if (hits.length === 0) {
        setStage("idle");
        setErr({ code: "empty", message: "I couldn't find that in the indexed code." });
        setBusy(false);
        return;
      }
      // 2) The single billed call — sends ONLY those snippets + the question (BYOK).
      setStage("asking");
      const r = await askCodebase(trimmed, hits);
      if (!r.answer) {
        setErr({ code: "empty", message: "I couldn't find that in the indexed code." });
      } else {
        setAnswer(r.answer);
        setCitations(r.citations);
        setConfidence(r.confidence);
        // Persist to local Q&A history (on-device only).
        try {
          await store.addHistory(repo.id, repo.name, trimmed, r.answer, r.citations.map((c) => snippetCite(c)));
        } catch { /* history is best-effort, never block the answer */ }
      }
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "Something went wrong — please try again." });
    } finally {
      setStage("idle");
      setBusy(false);
    }
  }

  function copyAnswer() {
    if (!answer) return;
    const cites = citations.map((c) => snippetCite(c)).join("; ");
    const text = cites ? `${answer}\n\nSources: ${cites}` : answer;
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div>
      {/* Design port 2026-08-26 (deliverable screens 03–05): ONE input card carrying the promise in
          its placeholder, a scope-pill + trust-badge row inside it, and the Ask/Search segmented
          control BELOW the card. Same two co-equal modes, same handlers — surface only. */}
      <form onSubmit={(e) => { e.preventDefault(); if (mode === "search") void runSearch(); else void run(); }}>
        <div className="kc-input-card">
          <div className="kc-input-row">
            <Search size={15} style={{ color: q.trim() ? "var(--accent)" : "var(--text-tertiary)", flexShrink: 0 }} />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              disabled={busy || searching}
              placeholder={mode === "search" ? "Search your code — e.g. parseCitations" : offline ? "Offline — answering paused" : "Ask about this codebase…"}
              aria-label={mode === "search" ? "Search your indexed code" : "Ask a question about your codebase"}
            />
            <button type="submit" className="kc-go"
              aria-label={mode === "search" ? "Search" : "Ask"}
              disabled={mode === "ask" ? (busy || offline || !q.trim()) : (searching || !q.trim())}>
              {(busy || searching) ? <Spinner /> : <ArrowRight size={18} />}
            </button>
          </div>
          <div className="row" style={{ justifyContent: "space-between", gap: "0.5rem", flexWrap: "wrap" }}>
            {repos.length > 1 ? (
              <label className="kc-pill accent" title="Which repo to ask about">
                <span aria-hidden>asking</span>
                <select className="kc-scope-select" value={activeId ?? ""} onChange={(e) => onSwitchRepo(e.target.value)} aria-label="Which repo to ask about">
                  {repos.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </label>
            ) : (
              <span className="kc-pill accent"><span className="kc-pill-txt">asking {repo.name}</span></span>
            )}
            {/* The trust mark: index freshness + size beside the question (Ask) / the free tag (Search). */}
            {mode === "search" ? (
              <span className="kc-pill accent"><span className="kc-pill-txt">Free · on-device</span></span>
            ) : (
              <span className="kc-pill" title="When this repo was indexed on this device">
                <span className="kc-dot" aria-hidden />
                <span className="kc-pill-txt tnum">Indexed {relativeTime(repo.updated_at) || "just now"} · {fmtCount(repo.file_count)} files</span>
              </span>
            )}
          </div>
        </div>
      </form>

      <div className="row" style={{ gap: "0.5rem", alignItems: "stretch", marginTop: 12 }}>
        <div className="kc-seg" role="tablist" aria-label="Ask or search your codebase" style={{ flex: 1 }}>
          <button type="button" role="tab" aria-selected={mode === "ask"} className={`kc-seg-btn${mode === "ask" ? " selected" : ""}`} onClick={() => setMode("ask")}>Ask</button>
          <button type="button" role="tab" aria-selected={mode === "search"} className={`kc-seg-btn${mode === "search" ? " selected" : ""}`} onClick={() => setMode("search")}>Search</button>
        </div>
        {/* Snap code: photograph a snippet/error/diagram → the brain reads its text → search the index. */}
        <SnapCodeButton onPhoto={(url) => void onSnap(url)} busy={snapping} disabled={busy || searching} />
      </div>
      {snapErr && (
        <div className="banner" style={{ marginTop: "0.6rem", background: "var(--bg-muted)", border: "1px solid var(--border-hairline)" }}>
          <Camera size={15} style={{ color: "var(--text-tertiary)", flexShrink: 0, marginTop: 1 }} />
          <span className="muted" style={{ fontSize: "var(--text-xs)", lineHeight: 1.5 }}>{snapErr}</span>
        </div>
      )}

      {/* Free search results — on-device, unbilled. */}
      {mode === "search" && (
        <>
          {searching && (
            <div className="result-card" style={{ marginTop: "0.85rem", padding: "1rem", display: "grid", gap: 8 }}>
              <div className="skeleton-line" style={{ width: "80%" }} />
              <div className="skeleton-line" style={{ width: "60%" }} />
            </div>
          )}
          {!searching && searchResults && searchResults.length === 0 && (
            <div className="banner" style={{ marginTop: "0.85rem", background: "var(--bg-muted)", border: "1px solid var(--border-hairline)" }}>
              <Search size={15} style={{ color: "var(--text-tertiary)", flexShrink: 0, marginTop: 1 }} />
              <span className="muted" style={{ fontSize: "var(--text-xs)", lineHeight: 1.5 }}>No matches in the indexed code. Try a symbol or file name.</span>
            </div>
          )}
          {!searching && searchResults && searchResults.length > 0 && (
            <div style={{ marginTop: "0.85rem", display: "grid", gap: "0.5rem" }}>
              {/* Design port: results kicker + hit cards with the searched term marked in accent. */}
              <div className="kicker">{searchResults.length} result{searchResults.length === 1 ? "" : "s"} <span className="dim" style={{ textTransform: "none", letterSpacing: 0 }}>· on-device, unbilled</span></div>
              {searchResults.map((s, i) => (
                <div key={i} className="kc-hit">
                  <div className="row" style={{ justifyContent: "space-between", gap: "0.5rem" }}>
                    <span className="kc-ev-file tnum" style={{ fontSize: "12.5px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}><FileCode size={11} style={{ verticalAlign: "-2px", marginRight: 4, color: "var(--accent)" }} />{snippetCite(s)}</span>
                    <span className="dim" style={{ fontSize: 10, flexShrink: 0 }}>{langLabel(s.lang)}</span>
                  </div>
                  <pre className="kc-hit-code">{markMatches(s.code, searchedFor)}</pre>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Helper line + "Try asking" example rows (Ask empty state only) — deliverable screen 03. */}
      {mode === "ask" && !busy && !answer && !err && (
        <>
          <div className="kicker" style={{ marginTop: "1.25rem" }}>Try asking</div>
          <div style={{ display: "grid", gap: "0.5rem", marginTop: "0.6rem" }}>
            {EXAMPLES.map((ex) => (
              <button key={ex} type="button" className="kc-example" disabled={offline}
                onClick={() => { setQ(ex); void run(ex); }}>
                <span>{ex}</span>
                <ArrowRight size={15} className="arrow" />
              </button>
            ))}
          </div>
          <div className="caption" style={{ marginTop: "0.75rem", display: "flex", gap: "0.35rem", alignItems: "center" }}>
            <ShieldCheck size={12} style={{ color: "var(--success)", flexShrink: 0 }} />
            Only the few matching snippets leave your device — never the whole repo. Answers cite file&nbsp;:&nbsp;line.
          </div>
        </>
      )}

      {/* Agentic progress: two-stage (retrieving on-device → asking the brain). */}
      {busy && (
        <div aria-live="polite" className="result-card" style={{ marginTop: "0.85rem", padding: "1rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 10 }}>
          <span className="dim row" style={{ fontSize: "var(--text-xs)", gap: "0.4rem" }}>
            {stage === "retrieving"
              ? <><Search size={12} style={{ color: "var(--accent)" }} /> Searching the index on-device…</>
              : <><Sparkles size={12} style={{ color: "var(--accent)" }} /> Reading {snippets.length} matched snippet{snippets.length === 1 ? "" : "s"}…</>}
          </span>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "88%" }} />
          <div className="skeleton-line" style={{ width: "64%" }} />
        </div>
      )}

      {err && !busy && <AskError err={err} onRetry={() => run()} />}

      {answer && !busy && (
        <div className="result-card" style={{ marginTop: "0.85rem", padding: "1rem 1.1rem" }} aria-live="polite">
          <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.5rem" }}>
            <span className="kicker" style={{ color: "var(--text-tertiary)" }}>Answer</span>
            <button className="btn-ghost" style={{ padding: "0.25rem 0.5rem", fontSize: "var(--text-xs)" }} onClick={copyAnswer} aria-live="polite">
              {copied ? <><Check size={13} color="var(--success)" /> Copied</> : <><Copy size={13} /> Copy</>}
            </button>
          </div>
          <p style={{ fontSize: "var(--text-base)", color: "var(--text-primary)", lineHeight: 1.6, margin: 0, whiteSpace: "pre-wrap" }}>{answer}</p>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>

          {/* Tappable file:line citations — tapping one opens its snippet in the evidence rail. */}
          {citations.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem", marginTop: "0.7rem" }}>
              {citations.map((c, i) => (
                <button key={`${c.path}-${i}`} type="button" className="kc-cite tnum" title={c.why}
                  onClick={() => {
                    const idx = evidenceIndexFor(c);
                    if (idx >= 0) {
                      setOpenEv(idx);
                      document.getElementById(`kc-ev-${idx}`)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
                    }
                  }}>
                  <FileCode size={11} /> {snippetCite(c)}
                </button>
              ))}
            </div>
          )}

          {/* THE SIGNATURE SURFACE (deliverable screen 04): the evidence rail — the answer rendered
              AGAINST the actual code. Each grounding snippet expands to its real lines, cited line
              ranges highlighted. These snippets are the only code that left the device. */}
          {snippets.length > 0 && (
            <div style={{ marginTop: "0.85rem", paddingTop: "0.75rem", borderTop: "1px solid var(--border-hairline)" }}>
              <div className="kicker" style={{ marginBottom: "0.5rem" }}>
                Evidence · {snippets.length} snippet{snippets.length === 1 ? "" : "s"}
                <span className="dim" style={{ textTransform: "none", letterSpacing: 0, fontFamily: "var(--font-body)", fontWeight: 400 }}> — the only code that left your device</span>
              </div>
              <div className="kc-evidence">
                {snippets.map((s, i) => (
                  <div key={i} id={`kc-ev-${i}`}>
                    <button type="button" className="kc-ev-head" aria-expanded={openEv === i}
                      onClick={() => setOpenEv(openEv === i ? -1 : i)}>
                      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={snippetCite(s)}>
                        <span className="kc-ev-file">{s.path.split("/").pop()}</span>
                        <span className="kc-ev-lines tnum">· lines {s.start_line}–{s.end_line}</span>
                      </span>
                      <span className="kc-ev-caret" aria-hidden>{openEv === i ? "▾" : "▸"}</span>
                    </button>
                    {openEv === i && (
                      <div className="kc-code" role="region" aria-label={snippetCite(s)}>
                        {s.code.split("\n").slice(0, 60).map((ln, j) => {
                          const n = s.start_line + j;
                          const cited = citations.some((c) => c.path === s.path && n >= c.start_line && n <= c.end_line);
                          return (
                            <div key={j} className={`ln-row${cited ? " hl" : ""}`}>
                              <span className="ln tnum">{n}</span><span className="src">{ln || " "}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {confidence > 0 && (
            <div style={{ marginTop: "0.85rem" }}>
              <div className="row" style={{ justifyContent: "space-between", marginBottom: 4 }}>
                <span className="caption">Grounding confidence</span>
                <span className="caption tnum">{Math.round(confidence * 100)}%</span>
              </div>
              <div className="conf-track"><div className="conf-fill" style={{ width: `${Math.round(confidence * 100)}%` }} /></div>
              {confidence < 0.4 && (
                <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>Low confidence — this may not be clearly stated in the indexed code.</div>
              )}
            </div>
          )}

          <div style={{ marginTop: "0.85rem" }}>
            <span className="dim" style={{ fontSize: 10 }}>Grounded in your code — verify against the cited lines before relying on it.</span>
          </div>
        </div>
      )}
    </div>
  );
}

function AskError({ err, onRetry }: { err: AskErr; onRetry: () => void }) {
  // Insufficient funds — point to the Oriora account, NO amount shown.
  if (err.code === "insufficient_funds") {
    return (
      <div className="banner banner-accent" style={{ marginTop: "0.85rem" }}>
        <Wallet size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>Add funds to keep asking</div>
          <div className="muted" style={{ fontSize: "var(--text-xs)", marginTop: 2 }}>Add funds in your Oriora account, then ask again.</div>
        </div>
      </div>
    );
  }
  // BYOK gate — Managed AI blocked for this BYOK-only app; needs a vendor key.
  if (err.code === "managed_ai_blocked") {
    return (
      <div className="banner banner-info" style={{ marginTop: "0.85rem" }}>
        <KeyRound size={16} style={{ color: "var(--info)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>Connect your vendor key</div>
          <div className="muted" style={{ fontSize: "var(--text-xs)", marginTop: 2 }}>This is a bring-your-own-key app. Connect your AI vendor key in your Oriora Settings, then ask again.</div>
        </div>
      </div>
    );
  }
  // Empty result — gentle, no retry (more/different words is the fix).
  if (err.code === "empty") {
    return (
      <div className="banner" style={{ marginTop: "0.85rem", background: "var(--bg-muted)", border: "1px solid var(--border-hairline)" }}>
        <Search size={15} style={{ color: "var(--text-tertiary)", flexShrink: 0, marginTop: 1 }} />
        <div className="muted" style={{ fontSize: "var(--text-xs)", lineHeight: 1.5 }}>{err.message} Try a function or file name, or re-index the folder if the code changed.</div>
      </div>
    );
  }
  // Network / session / unavailable — recoverable, offer a retry.
  const retryable = err.code === "unavailable";
  return (
    <div className="banner banner-danger" style={{ marginTop: "0.85rem", justifyContent: "space-between", alignItems: "center" }}>
      <span className="row" style={{ gap: "0.45rem" }}>
        <AlertTriangle size={15} style={{ color: "var(--danger)", flexShrink: 0 }} />
        <span style={{ fontSize: "var(--text-xs)" }}>{err.message}</span>
      </span>
      {retryable && (
        <button className="btn" style={{ fontSize: "var(--text-xs)", padding: "0.3rem 0.6rem", flexShrink: 0 }} onClick={onRetry}>
          <RefreshCw size={12} /> Retry
        </button>
      )}
    </div>
  );
}

// ── Symbol map (deterministic, on-device — "where is X defined?") ──
function SymbolMap({ repo }: { repo: Repo }) {
  const [filter, setFilter] = useState("");
  const [symbols, setSymbols] = useState<Symbol[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    store.allSymbols(repo.id, 400)
      .then((s) => { if (alive) setSymbols(s); })
      .catch(() => { if (alive) setSymbols([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [repo.id]);

  const shown = useMemo(() => {
    if (!symbols) return [];
    const f = filter.trim().toLowerCase();
    const list = f ? symbols.filter((s) => s.name.toLowerCase().includes(f)) : symbols;
    return list.slice(0, 60);
  }, [symbols, filter]);

  if (loading) {
    return (
      <div className="card" style={{ padding: "1.1rem 1.25rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
        <div className="skeleton-line" style={{ width: "30%", height: 14 }} />
        <div className="skeleton-line" style={{ width: "85%" }} />
        <div className="skeleton-line" style={{ width: "60%" }} />
      </div>
    );
  }
  if (!symbols || symbols.length === 0) {
    return (
      <div className="card" style={{ padding: "1.1rem 1.25rem" }}>
        <div className="row" style={{ gap: "0.4rem", marginBottom: "0.5rem" }}>
          <Braces size={16} style={{ color: "var(--accent)" }} />
          <div style={{ fontWeight: 600, fontSize: "var(--text-sm)" }}>Symbol map</div>
        </div>
        <div className="muted" style={{ fontSize: "var(--text-xs)", lineHeight: 1.5 }}>
          No functions, classes or structs were parsed from this repo's files. The map fills in as you
          index source-heavy projects.
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: "1.1rem 1.25rem" }}>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.75rem", gap: "0.4rem", flexWrap: "wrap" }}>
        <div className="row" style={{ gap: "0.4rem" }}>
          <Braces size={16} style={{ color: "var(--accent)" }} />
          <div style={{ fontWeight: 600, fontSize: "var(--text-sm)" }}>Symbol map</div>
          <span className="dim" style={{ fontSize: "var(--text-xs)" }}>· {fmtCount(symbols.length)} parsed on-device</span>
        </div>
        <div style={{ position: "relative", minWidth: 0 }}>
          <Search size={13} style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "var(--text-tertiary)" }} />
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Jump to a symbol…" aria-label="Filter symbols"
            style={{ paddingLeft: 28, fontSize: "var(--text-xs)", padding: "5px 8px 5px 28px", width: 180 }} />
        </div>
      </div>
      {shown.length === 0 ? (
        <div className="muted" style={{ fontSize: "var(--text-xs)" }}>No symbol matches “{filter}”.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.3rem" }}>
          {shown.map((s) => (
            <div key={s.id} className="kd-row" style={{ alignItems: "center" }}>
              <span className="kd-pill later" style={{ minWidth: 46, textAlign: "center" }}>{SYMBOL_KIND_LABELS[s.kind] ?? s.kind}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="tnum" style={{ fontWeight: 600, fontSize: "var(--text-sm)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</div>
                <div className="dim tnum" style={{ fontSize: "var(--text-xs)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.path}:{s.line}</div>
              </div>
              <span className="dim" style={{ fontSize: "var(--text-xs)", flexShrink: 0 }}>{langLabel(s.lang)}</span>
            </div>
          ))}
          {symbols.length > shown.length && !filter && (
            <div className="dim" style={{ fontSize: "var(--text-xs)", marginTop: 8 }}>+{fmtCount(symbols.length - shown.length)} more — filter to find a specific one</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── File list (what got indexed; provenance the user can scan) ──
function FileList({ repo }: { repo: Repo }) {
  const [files, setFiles] = useState<FileRow[] | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    store.listFiles(repo.id, 300).then((f) => { if (alive) setFiles(f); }).catch(() => { if (alive) setFiles([]); });
    return () => { alive = false; };
  }, [repo.id]);

  if (!files || files.length === 0) return null;
  const shown = open ? files : files.slice(0, 8);
  return (
    <div className="card" style={{ padding: "1.1rem 1.25rem", marginTop: "1.5rem" }}>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.75rem" }}>
        <div className="row" style={{ gap: "0.4rem" }}>
          <FileCode size={16} style={{ color: "var(--accent)" }} />
          <div style={{ fontWeight: 600, fontSize: "var(--text-sm)" }}>Indexed files</div>
          <span className="dim" style={{ fontSize: "var(--text-xs)" }}>· {fmtCount(repo.file_count)}</span>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.3rem" }}>
        {shown.map((f) => (
          <div key={f.path} className="row" style={{ justifyContent: "space-between", gap: "0.5rem" }}>
            <span className="tnum" style={{ fontSize: "var(--text-xs)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>{f.path}</span>
            <span className="dim tnum" style={{ fontSize: "var(--text-xs)", flexShrink: 0 }}>{langLabel(f.lang)} · {fmtBytes(f.byte_count)}</span>
          </div>
        ))}
      </div>
      {files.length > 8 && (
        <button className="link-btn" style={{ marginTop: 10, fontSize: "var(--text-xs)" }} onClick={() => setOpen((o) => !o)}>
          {open ? "Show fewer" : `Show all ${fmtCount(files.length)}`}
        </button>
      )}
    </div>
  );
}

// ── Local Q&A history (on-device only) ──
function HistoryPanel({ onClose }: { onClose: () => void }) {
  const [items, setItems] = useState<HistoryEntry[] | null>(null);

  const load = useCallback(() => {
    store.listHistory(200).then(setItems).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function clearAll() {
    await store.clearHistory();
    setItems([]);
  }
  async function remove(id: string) {
    await store.deleteHistory(id);
    setItems((prev) => prev?.filter((h) => h.id !== id) ?? null);
  }

  return (
    <ModalShell title="Question history" onCancel={onClose} wide>
      {items === null ? (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
          {[0, 1, 2].map((i) => <div key={i} className="skeleton-line" style={{ width: i === 1 ? "70%" : "100%", height: 14 }} />)}
        </div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: "center", padding: "1.5rem 0.5rem" }}>
          <History size={26} color="var(--text-tertiary)" />
          <div className="muted" style={{ fontSize: "var(--text-sm)", marginTop: "0.5rem", lineHeight: 1.6 }}>
            No questions yet. Your asked questions and answers are saved here on this device.
          </div>
        </div>
      ) : (
        <>
          <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.75rem" }}>
            <span className="caption">{fmtCount(items.length)} on this device</span>
            <button className="link-btn" style={{ fontSize: "var(--text-xs)" }} onClick={clearAll}>Clear all</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.6rem", maxHeight: "60vh", overflowY: "auto" }}>
            {items.map((h) => {
              const cites = parseCitations(h.citations);
              return (
                <div key={h.id} className="card" style={{ padding: "0.75rem 0.9rem" }}>
                  <div className="row" style={{ justifyContent: "space-between", gap: "0.5rem", alignItems: "flex-start" }}>
                    <div className="row" style={{ gap: "0.35rem", fontWeight: 600, fontSize: "var(--text-sm)", minWidth: 0 }}>
                      <ArrowRight size={13} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 3 }} />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{h.question}</span>
                    </div>
                    <button className="btn-ghost" aria-label="Delete this entry" style={{ padding: "0.2rem", color: "var(--text-tertiary)", flexShrink: 0 }} onClick={() => remove(h.id)}>
                      <X size={13} />
                    </button>
                  </div>
                  <div className="muted" style={{ fontSize: "var(--text-xs)", lineHeight: 1.55, margin: "0.35rem 0 0", whiteSpace: "pre-wrap" }}>{h.answer}</div>
                  <div className="row" style={{ justifyContent: "space-between", marginTop: "0.5rem", gap: "0.4rem", flexWrap: "wrap" }}>
                    <div className="row" style={{ gap: "0.3rem", flexWrap: "wrap" }}>
                      {cites.slice(0, 3).map((c, i) => (
                        <span key={i} className="cite tnum" style={{ fontSize: 10 }}><FileCode size={10} /> {c}</span>
                      ))}
                    </div>
                    <span className="dim" style={{ fontSize: 10, flexShrink: 0 }}>{h.repo_name} · {relativeTime(h.created_at)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </ModalShell>
  );
}

function ModalShell({ title, children, onCancel, wide }: { title: string; children: React.ReactNode; onCancel: () => void; wide?: boolean }) {
  // Esc closes; backdrop click closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div role="dialog" aria-modal="true" aria-label={title}
      onClick={onCancel}
      style={{ position: "fixed", inset: 0, background: "var(--bg-overlay)", backdropFilter: "blur(2px)", display: "grid", placeItems: "center", zIndex: 50, padding: "1rem" }}>
      {/* fill-then-cap, not cap-then-fill: width:N + maxWidth:100% resolved the 100% against the
          centred grid track rather than the viewport, so the sheet rendered full-width on a 402pt
          phone with its controls off-screen. */}
      <div className="card-raised" onClick={(e) => e.stopPropagation()}
        style={{ width: "100%", maxWidth: wide ? 520 : 360, padding: "1.5rem", boxShadow: "var(--shadow-lg)", maxHeight: "90vh", overflowY: "auto" }}>
        <div className="row" style={{ justifyContent: "space-between", marginBottom: "1.25rem" }}>
          <div className="h3" style={{ margin: 0 }}>{title}</div>
          <button className="btn-ghost" aria-label="Close" style={{ padding: "0.3rem" }} onClick={onCancel}><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Mark the searched term inside a hit's code (design port 2026-08-26, screen 05).
//    Presentational only — plain string split, regex-escaped, renders React text nodes.
function markMatches(code: string, term: string): React.ReactNode {
  const t = term.trim();
  if (!t) return code;
  let re: RegExp;
  try { re = new RegExp(`(${t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig"); } catch { return code; }
  const parts = code.split(re);
  if (parts.length === 1) return code;
  return parts.map((p, i) => (i % 2 === 1 ? <span key={i} className="kc-mark">{p}</span> : p));
}

// ── Tiny inline spinner (button "Working…" state, §7.2) ─────────────────────────
function Spinner() {
  return (
    <span aria-hidden style={{
      width: 13, height: 13, borderRadius: 999, display: "inline-block",
      border: "2px solid currentColor", borderTopColor: "transparent",
      animation: "spin 0.7s linear infinite", verticalAlign: "-1px", marginRight: 2,
    }} />
  );
}
